import { LogUsage } from '@/components/LogUsage'
import { QRScannerDialog, type QRScannerDialogRef } from '@/components/QRScannerDialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import type { ItemStatus } from '@/constants/item'
import { PatientCard } from '@/features/patients/components/PatientCard'
import {
  PatientSearchSelector,
  type PatientSearchSelectorRef,
} from '@/features/patients/components/search/PatientSearchSelector'
import type { Patient } from '@/features/patients/types/patient'
import type { ItemLogUsage } from '@/types/itemLogUsage'
import { showError } from '@/utils/toast'
import { ScanLine } from 'lucide-react'
import { type Dispatch, type RefObject, type SetStateAction, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'

type UsageForm = { patientId: string; note: string }

export function UsageSection({
  itemStatus,
  form,
  setForm,
  patientSearchSelectorRef,
  selectedPatient,
  isLoadingSelectedPatient,
  usageLogs,
  isCreatingUsage,
  isEndingUsage,
  onSelectPatient,
  onScanPatient,
  onUsageLog,
  onOpenEndUsageDialog,
}: {
  itemStatus: ItemStatus
  form: UsageForm
  setForm: Dispatch<SetStateAction<UsageForm>>
  patientSearchSelectorRef: RefObject<PatientSearchSelectorRef | null>
  selectedPatient?: Patient
  isLoadingSelectedPatient?: boolean
  usageLogs: ItemLogUsage[]
  isCreatingUsage: boolean
  isEndingUsage: boolean
  onSelectPatient: (patient: Patient | null) => void
  onScanPatient: (patientId: string) => void
  onUsageLog: () => void
  onOpenEndUsageDialog: () => void
}) {
  const { t } = useTranslation()
  const scannerRef = useRef<QRScannerDialogRef>(null)

  const handleScan = useCallback(
    (text: string) => {
      try {
        const url = new URL(text)
        const match = url.pathname.match(/\/scan\/patient\/([^/]+)/)
        const patientId = match?.[1]

        if (!patientId) {
          showError(t('common.qrScanner.invalidQr'))
          return
        }

        onScanPatient(patientId)
      } catch {
        showError(t('common.qrScanner.invalidQr'))
      }
    },
    [onScanPatient, t],
  )

  return (
    <>
      {itemStatus === 'Available' ? (
        <div className="mb-4 flex flex-col gap-3" data-testid="usage-form">
          <Button
            data-testid="usage-scan-patient-button"
            type="button"
            variant="outline"
            size="icon"
            className="shrink-0 absolute top-1.5 right-1.5"
            onClick={() => scannerRef.current?.start()}
            title={t('common.qrScanner.scanButton')}
          >
            <ScanLine className="size-4" />
          </Button>
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs">{t('scans.field.patient')} *</Label>
            <PatientSearchSelector ref={patientSearchSelectorRef} onSelect={onSelectPatient} testId="usage-patient-selector" />
          </div>

          {selectedPatient || isLoadingSelectedPatient ? (
            <PatientCard patient={selectedPatient} variant="selected" isLoading={isLoadingSelectedPatient} />
          ) : null}

          <div className="flex flex-col gap-1.5">
            <Label className="text-xs">{t('scans.field.note')} *</Label>
            <Textarea
              data-testid="usage-note"
              value={form.note}
              onChange={e => setForm(current => ({ ...current, note: e.target.value }))}
              className="bg-secondary/50 text-xs"
              rows={2}
              placeholder={t('scans.placeholder.usageNote')}
            />
          </div>

          <Button
            data-testid="usage-submit-button"
            onClick={onUsageLog}
            className="text-xs"
            disabled={!form.patientId || !selectedPatient?.bedId || !form.note.trim() || isCreatingUsage}
          >
            {isCreatingUsage ? t('scans.usage.saving') : t('scans.usage.save')}
          </Button>
        </div>
      ) : itemStatus === 'InUse' ? (
        <div className="mb-4 flex flex-col gap-3" data-testid="usage-form">
          <Button data-testid="usage-end-button" onClick={onOpenEndUsageDialog} className="text-xs" variant="destructive" disabled={isEndingUsage}>
            {t('scans.usage.stop')}
          </Button>
        </div>
      ) : null}
      <LogUsage usageLogs={usageLogs}></LogUsage>

      <QRScannerDialog ref={scannerRef} onScan={handleScan} />
    </>
  )
}
